/**
 * <product-customization>
 *
 * Single-step pattern picker rendered inside a `main-product` block.
 * Customers choose one pattern from a metaobject-backed grid; the
 * confirmed pattern's `property` field is captured into a hidden
 * input that the theme mirrors into the Add-to-Cart form as a line
 * item property.
 *
 * Selection capture:
 *   The component owns a hidden <input data-product-property> that
 *   theme.ProductProperties (assets/theme.js) auto-mirrors into the
 *   Add-to-Cart form. We update its value and dispatch `change`.
 *   When the customer picks "No customization", we write an empty
 *   string — Shopify drops empty line item properties on submit, so
 *   the order line item is left clean.
 *
 * Data model (per option entry from the metaobject):
 *   {
 *     id, category, variant_name, design,
 *     title, property, description, image
 *   }
 */

if (!customElements.get('product-customization')) {
  class ProductCustomization extends HTMLElement {
    constructor() {
      super();
      this.options = [];
      this.pendingIndex = null;
      this.confirmed = null;
    }

    connectedCallback() {
      this.modal = this.querySelector('[data-pcustom-modal]');
      this.grid = this.querySelector('[data-pcustom-grid]');
      this.summary = this.querySelector('[data-pcustom-summary]');
      this.summaryCode = this.querySelector('[data-pcustom-summary-code]');
      this.summaryTitle = this.querySelector('[data-pcustom-summary-title]');
      this.confirmBtn = this.querySelector('[data-pcustom-confirm]');
      this.statusEl = this.querySelector('[data-pcustom-status]');
      this.propertyInput = this.querySelector('[data-product-property]');
      this.modeRadios = this.querySelectorAll('[data-pcustom-mode]');

      this.#hydrateOptions();
      this.#bindEvents();
    }

    #hydrateOptions() {
      const payload = this.querySelector('[data-pcustom-options]');
      if (!payload) return;

      try {
        this.options = JSON.parse(payload.textContent || '[]');
      } catch (_e) {
        this.options = [];
      }
    }

    #bindEvents() {
      this.addEventListener('change', (event) => {
        if (event.target.matches('[data-pcustom-mode]')) this.#onModeChange(event.target.value);
      });

      this.addEventListener('click', (event) => {
        const tile = event.target.closest('[data-pcustom-select]');

        if (tile) {
          event.preventDefault();
          this.#onPatternSelect(tile);
          return;
        }

        if (event.target.closest('[data-pcustom-open]')) {
          event.preventDefault();
          this.#openModal();
          return;
        }

        if (event.target.closest('[data-pcustom-close]') || event.target.closest('[data-pcustom-cancel]')) {
          event.preventDefault();
          this.#closeModal(false);
          return;
        }

        if (event.target.closest('[data-pcustom-confirm]')) {
          event.preventDefault();
          this.#confirm();
          return;
        }
      });

      this.modal?.addEventListener('cancel', (event) => {
        event.preventDefault();
        this.#closeModal(false);
      });

      this.modal?.addEventListener('click', (event) => {
        if (event.target === this.modal) this.#closeModal(false);
      });
    }

    #onModeChange(mode) {
      if (mode === 'customize') {
        if (this.confirmed) {
          this.#renderSummary(this.confirmed);
          this.#writeProperty(this.confirmed.property);
          return;
        }

        this.#openModal();
        return;
      }

      this.confirmed = null;
      this.pendingIndex = null;
      this.#hideSummary();
      this.#paintTiles();
      this.#writeProperty('');
    }

    #openModal() {
      if (!this.modal) return;

      this.pendingIndex = this.confirmed ? this.confirmed.index : null;
      this.#paintTiles();
      this.#refreshConfirmState();

      if (typeof this.modal.showModal === 'function') {
        this.modal.showModal();
      } else {
        this.modal.setAttribute('open', '');
      }

      this.#lockBodyScroll();
    }

    #closeModal(keepConfirmed) {
      if (!this.modal) return;

      if (typeof this.modal.close === 'function') {
        this.modal.close();
      } else {
        this.modal.removeAttribute('open');
      }

      this.#unlockBodyScroll();

      if (!keepConfirmed && !this.confirmed) {
        const noneRadio = this.querySelector('[data-pcustom-mode][value="none"]');
        if (noneRadio) noneRadio.checked = true;
        this.#writeProperty('');
      }
    }

    #lockBodyScroll() {
      const { documentElement, body } = document;
      if (body.dataset.pcustomScrollLocked === 'true') return;

      this.savedScrollY = window.scrollY;
      body.dataset.pcustomScrollLocked = 'true';
      body.dataset.pcustomScrollY = String(this.savedScrollY);
      body.style.position = 'fixed';
      body.style.top = `-${this.savedScrollY}px`;
      body.style.left = '0';
      body.style.right = '0';
      body.style.width = '100%';
      documentElement.style.scrollbarGutter = 'stable';
    }

    #unlockBodyScroll() {
      const { documentElement, body } = document;
      if (body.dataset.pcustomScrollLocked !== 'true') return;

      const savedY = Number(body.dataset.pcustomScrollY || 0);

      delete body.dataset.pcustomScrollLocked;
      delete body.dataset.pcustomScrollY;
      body.style.position = '';
      body.style.top = '';
      body.style.left = '';
      body.style.right = '';
      body.style.width = '';
      documentElement.style.scrollbarGutter = '';

      window.scrollTo(0, savedY);
    }

    #onPatternSelect(button) {
      const index = Number(button.dataset.entryIndex);
      if (Number.isNaN(index)) return;

      this.pendingIndex = index;
      this.#paintTiles();
      this.#refreshConfirmState();
    }

    #paintTiles() {
      if (!this.grid) return;

      this.grid.querySelectorAll('[data-pcustom-select]').forEach((tile) => {
        const isActive = Number(tile.dataset.entryIndex) === this.pendingIndex;
        tile.setAttribute('aria-pressed', isActive ? 'true' : 'false');
      });
    }

    #refreshConfirmState() {
      const entry = this.#pendingEntry();
      const valid = Boolean(entry);

      if (this.confirmBtn) this.confirmBtn.disabled = !valid;

      if (this.statusEl) {
        if (!valid) {
          this.statusEl.textContent = '';
          return;
        }

        const parts = [entry.variant_name, entry.title].filter(Boolean);
        const selectedLabel = this.statusEl.dataset.selectedLabel || 'Selected';
        this.statusEl.textContent = parts.length
          ? `${selectedLabel}: ${parts.join(' · ')}`
          : '';
      }
    }

    #confirm() {
      const entry = this.#pendingEntry();
      if (!entry) return;

      const propertyValue = entry.property
        || [entry.variant_name, entry.title].filter(Boolean).join(' · ')
        || entry.title
        || '';

      this.confirmed = {
        index: this.pendingIndex,
        code: entry.variant_name || '',
        title: entry.title || '',
        property: propertyValue,
      };

      this.#renderSummary(this.confirmed);
      this.#writeProperty(propertyValue);
      this.#closeModal(true);
    }

    #pendingEntry() {
      if (this.pendingIndex == null) return null;
      return this.options[this.pendingIndex] || null;
    }

    #renderSummary({ code, title }) {
      if (!this.summary) return;

      if (this.summaryCode) {
        this.summaryCode.textContent = code || '';
        this.summaryCode.hidden = !code;
      }

      if (this.summaryTitle) this.summaryTitle.textContent = title || '';

      this.summary.hidden = false;
    }

    #hideSummary() {
      if (this.summary) this.summary.hidden = true;
    }

    /**
     * Update the hidden [data-product-property] input. The theme's
     * ProductProperties handler (assets/theme.js) listens for the
     * `change` event and mirrors the value into a hidden input
     * inside the Add-to-Cart form.
     *
     * Empty string clears the property — Shopify drops empty line
     * item properties when the order is created.
     */
    #writeProperty(value) {
      if (!this.propertyInput) return;

      this.propertyInput.value = value || '';
      this.propertyInput.dispatchEvent(new Event('change', { bubbles: true }));
    }
  }

  customElements.define('product-customization', ProductCustomization);
}
